import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import sortBy from 'lodash/sortBy'

import {
  useFetchTicketProviderTicketFilterFieldsQuery,
} from 'api/ticketProviderTicketFilters'

import filterColumnTypes from 'constants/filterColumnTypes'

const datetimeAllowedValues = [
  { value: 'NEXT_30_DAYS', name: 'Next 30 days' },
  { value: 'NEXT_14_DAYS', name: 'Next 14 days' },
  { value: 'NEXT_7_DAYS', name: 'Next 7 days' },
  { value: 'TOMORROW', name: 'Tomorrow' },
  { value: 'LAST_HOUR', name: 'Last hour' },
  { value: 'TODAY', name: 'Today' },
  { value: 'YESTERDAY', name: 'Yesterday' },
  { value: 'THIS_WEEK_MON', name: 'This Week (Mon-Sun)' },
  { value: 'THIS_WEEK_SUN', name: 'This Week (Sun-Sat)' },
  { value: 'LAST_7_DAYS', name: 'Last 7 days' },
  { value: 'LAST_14_DAYS', name: 'Last 14 days' },
  { value: 'LAST_30_DAYS', name: 'Last 30 days' },
]

const getOptions = (values) => {
  return values.map(value => ({
    name: value,
    value: value,
  }))
}

const getSortedStatusesOptions = (partnerStatuses) => {
  let options = []
  partnerStatuses.forEach((status) => {
    options.push({
      name: status.name,
      value: status.name,
      color: status.color,
    })
  })

  options = sortBy(options, ['name'])
  return options
}

const getHardcodedFields = (partnerStatusesAllowedValues, optionLabels) => {
  const bondedColumn = {
    name: 'ticket_bonded',
    allowedValues: getOptions(['yes', 'no']),
    fieldType: filterColumnTypes.BONDED,
    isDynamic: true,
    isMultiSelect: false,
  }

  const createdByColumn = {
    name: 'created_by',
    allowedValues: [{
      value: 'is_me',
      name: optionLabels['is_me'],
    }],
    fieldType: filterColumnTypes.CREATEDBY,
    isDynamic: true,
    isMultiSelect: false,
  }

  const statusColumn = {
    name: 'status',
    allowedValues: partnerStatusesAllowedValues,
    fieldType: filterColumnTypes.STATUS,
    isMultiSelect: true,
  }

  return [bondedColumn, createdByColumn, statusColumn]
}

/*
   Fetches fields' descriptions, adds dynamic (custom) fields, extend them
   with additional parameters and sorts them by name.

   Field data example:
     {
       name: 'created_by',
       allowedValues: [{name: 'Is Me', value: 'Is Me'}],
       fieldType: 'created_by',
       isDynamic: true,
       isMultiSelect: true,
     }
*/
const getSortedFilterFields = ({ filterFieldsData, partnerStatusesAllowedValues, isGlobal, optionLabels }) => {
  if (!filterFieldsData) {
    return { filterFields: [], filterFieldsMap: {} }
  }

  const { sharedFieldMapFields } = filterFieldsData
  // presently isGlobal is always true but may be changed in future
  const filterFieldsOrig = isGlobal ? sharedFieldMapFields : []

  const hardcodedFields = getHardcodedFields(partnerStatusesAllowedValues, optionLabels)

  const filterFields = [
    ...filterFieldsOrig.map(field => {
      const fieldCopy = { ...field }

      // to reproduce current logic we use names instead of values here
      fieldCopy.allowedValues = fieldCopy.allowedValues.map(v => ({
        name: v.name,
        value: v.name,
      }))

      const isDateTime =
        (fieldCopy.fieldType === filterColumnTypes.DATETIME) ||
        (fieldCopy.fieldType === filterColumnTypes.DATE)

      // add predefined allowed values for DATETIME or DATE fields
      if (isDateTime) {
        fieldCopy.allowedValues = datetimeAllowedValues
      } else {
        const hasAllowedValues = fieldCopy.allowedValues?.length > 0
        const needAddMyCompany = fieldCopy.name === 'owner' || fieldCopy.name === 'resolving_partner'

        if (needAddMyCompany) {
          fieldCopy.allowedValues = [
            ...fieldCopy.allowedValues,
            {
              value: 'my_company',
              name: optionLabels['my_company'],
            },
          ]
          fieldCopy.fieldType = filterColumnTypes.SELECT
        }

        // set SELECT as field type for fields with allowedValues for different operator options
        if (hasAllowedValues) {
          fieldCopy.fieldType = filterColumnTypes.SELECT
        }
      }

      fieldCopy.isMultiSelect = !isDateTime
      return fieldCopy
    }),
    ...hardcodedFields,
  ]

  const sortedFields = sortBy(filterFields, field => field.name)

  const filterFieldsMap = {}
  sortedFields.forEach(field => { filterFieldsMap[field.name] = field })

  return { filterFields: sortedFields, filterFieldsMap }
}

export const useFilterFields = ({ ticketProviderId, isGlobal, partnerStatuses }) => {
  const { t } = useTranslation()
  const optionLabels = t('ticketProviderTicketsPage.filterForm.optionLabels', { returnObjects: true, useSuspense: false })

  const { data: filterFieldsData, isFetching } = useFetchTicketProviderTicketFilterFieldsQuery({
    ticketProviderId: ticketProviderId,
  })
  const partnerStatusesAllowedValues = useMemo(() => {
    return getSortedStatusesOptions(partnerStatuses)
  }, [partnerStatuses])

  // adding optionLabels creates infinite loop due to "t" changes
  const { filterFields, filterFieldsMap } = useMemo(() => {
    return getSortedFilterFields({ filterFieldsData, partnerStatusesAllowedValues, isGlobal, optionLabels })
  }, [filterFieldsData, partnerStatusesAllowedValues, isGlobal]) // eslint-disable-line react-hooks/exhaustive-deps

  return { filterFields, filterFieldsMap, isFetching }
}
